import { onMounted, onUnmounted } from 'vue';

export function useDragScroll() {
    let cleanups = [];

    const isChromeLike = (() => {
        try {
            const ua = navigator.userAgent || '';
            return /Chrome|CriOS/i.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua);
        } catch (e) {
            return false;
        }
    })();

    const enable = () => {
        if (!isChromeLike) return;
        const containers = document.querySelectorAll('[data-horizontal-scroll]');
        containers.forEach((el) => {
            let isDown = false;
            let dragged = false;
            let startX = 0;
            let startY = 0;
            let scrollLeft = 0;

            const hasPointer = typeof window !== 'undefined' && 'PointerEvent' in window;
            const getX = (e) => (e.touches ? e.touches[0].pageX : (e.clientX ?? e.pageX));
            const getY = (e) => (e.touches ? e.touches[0].pageY : (e.clientY ?? e.pageY));

            let capturedPointerId = null;

            const onDown = (e) => {
                isDown = true;
                dragged = false;
                startX = getX(e);
                startY = getY(e);
                scrollLeft = el.scrollLeft;
                el.classList.add('dragging');
            };

            const onMove = (e) => {
                if (!isDown) return;
                const dx = getX(e) - startX;
                const dy = getY(e) - startY;
                if (Math.abs(dx) > Math.abs(dy)) {
                    if (Math.abs(dx) > 4) {
                        dragged = true;
                        // Capture only once a real drag starts. Capturing on
                        // pointerdown makes Chrome retarget the follow-up click to
                        // this container, so clicks never reached the cards or
                        // article links inside it (MY1-1804 regression).
                        capturePointer(e);
                    }
                    e.preventDefault();
                    el.scrollLeft = scrollLeft - dx;
                }
            };

            const capturePointer = (e) => {
                if (!hasPointer || e.pointerId == null || !el.setPointerCapture) return;
                if (capturedPointerId === e.pointerId) return;
                try {
                    el.setPointerCapture(e.pointerId);
                    capturedPointerId = e.pointerId;
                } catch (_) {}
            };

            const releasePointer = () => {
                if (capturedPointerId == null) return;
                try { el.releasePointerCapture(capturedPointerId); } catch (_) {}
                capturedPointerId = null;
            };

            const onUp = () => {
                if (!isDown) return;
                isDown = false;
                releasePointer();
                setTimeout(() => { dragged = false; }, 0);
                el.classList.remove('dragging');
            };

            const onClickCapture = (e) => {
                if (dragged) { e.preventDefault(); e.stopPropagation(); }
            };

            el.addEventListener('click', onClickCapture, true);
            if (hasPointer) {
                el.addEventListener('pointerdown', onDown);
                el.addEventListener('pointermove', onMove, { passive: false });
                // Listen on the window, not the element: without capture on
                // pointerdown a release outside the container never reaches it,
                // which would leave the gesture stuck open.
                window.addEventListener('pointerup', onUp);
                window.addEventListener('pointercancel', onUp);
            } else {
                el.addEventListener('touchstart', onDown, { passive: true });
                el.addEventListener('touchmove', onMove, { passive: false });
                el.addEventListener('touchend', onUp, { passive: true });
                el.addEventListener('mousedown', onDown);
                el.addEventListener('mousemove', onMove);
                el.addEventListener('mouseleave', onUp);
                el.addEventListener('mouseup', onUp);
            }

            cleanups.push(() => {
                el.removeEventListener('click', onClickCapture, true);
                if (hasPointer) {
                    el.removeEventListener('pointerdown', onDown);
                    el.removeEventListener('pointermove', onMove);
                    window.removeEventListener('pointerup', onUp);
                    window.removeEventListener('pointercancel', onUp);
                } else {
                    el.removeEventListener('touchstart', onDown);
                    el.removeEventListener('touchmove', onMove);
                    el.removeEventListener('touchend', onUp);
                    el.removeEventListener('mousedown', onDown);
                    el.removeEventListener('mousemove', onMove);
                    el.removeEventListener('mouseleave', onUp);
                    el.removeEventListener('mouseup', onUp);
                }
            });
        });
    };

    onMounted(() => enable());

    onUnmounted(() => {
        cleanups.forEach((fn) => { try { fn(); } catch (e) {} });
        cleanups = [];
    });
}
